import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ProgramSignupModal from '../components/ProgramSignupModal';
import {
  CheckIcon,
  UserGroupIcon,
  ClockIcon,
  StarIcon,
  HeartIcon,
  MapPinIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const Programmes = () => {
  const { currentUser } = useAuth();
  
  // Helper functions to format complex objects
  const formatSchedule = (schedule) => {
    if (!schedule) return 'Contact us for more information';
    if (typeof schedule === 'string') return schedule;
    
    // Handle Kids' Athletics schedule (simple structure)
    if (schedule.day && schedule.time && schedule.frequency) {
      return `${schedule.day} at ${schedule.time} (${schedule.frequency})`;
    }
    
    // Handle Primary School Programme schedule (days/times arrays)
    if (schedule.days && schedule.times && schedule.frequency) {
      const dayTimePairs = schedule.days.map((day, index) => 
        `${day} at ${schedule.times[index] || 'TBA'}`
      ).join(', ');
      return `${dayTimePairs} (${schedule.frequency})`;
    }
    
    // Handle Junior Programme schedule (complex centers structure) - show contact us for complex info
    if (schedule.centers && Array.isArray(schedule.centers)) {
      // If there are multiple centers or senior squad, show contact us
      if (schedule.centers.length > 2 || schedule.senior_squad) {
        return 'Contact us for more information';
      }
      
      // For simple center structures, show the info
      const centerSchedules = schedule.centers.map(center => 
        `${center.day} at ${center.time}`
      ).join(', ');
      return centerSchedules;
    }
    
    // Fallback for other structures
    if (schedule.frequency) return schedule.frequency;
    if (schedule.day) return schedule.day;
    
    return 'Contact us for more information';
  };

  const formatVenue = (venue) => {
    if (!venue) return 'Contact us for more information';
    if (typeof venue === 'string') {
      // If venue string is too long or contains multiple locations, show contact us
      if (venue.length > 50 || venue.includes('Multiple locations') || venue.includes(',')) {
        return 'Contact us for more information';
      }
      return venue;
    }
    
    // Handle venue object structures
    if (venue.location) {
      return venue.location;
    }
    if (venue.name) {
      return venue.name;
    }
    
    return 'Contact us for more information';
  };

  const renderFees = (fees) => {
    if (!fees) return <span className="text-gray-400">TBA</span>;
    if (typeof fees === 'string' || typeof fees === 'number') {
      // Clean up any redundant text in string fees
      const cleanFee = String(fees).replace(/\([^)]*\)\/month/g, '').replace(/\/month/g, '/month');
      return <span className="text-oldham-gold font-semibold">{cleanFee}</span>;
    }
    
    // Handle Kids' Athletics fees (monthly + ad_hoc)
    if (fees.monthly && fees.ad_hoc) {
      return (
        <div className="space-y-1">
          <div className="text-oldham-gold font-semibold">
            {fees.monthly}
          </div>
          <div className="text-oldham-gold font-semibold">
            {fees.ad_hoc}
          </div>
        </div>
      );
    }
    
    // Handle Primary School Programme fees (once_weekly + twice_weekly)
    if (fees.once_weekly && fees.twice_weekly && !fees.term) {
      // Extract just the main price number from the fee strings
      const onceWeeklyMatch = String(fees.once_weekly).match(/\$?(\d+)/);
      const twiceWeeklyMatch = String(fees.twice_weekly).match(/\$?(\d+)/);
      
      const onceWeekly = onceWeeklyMatch ? onceWeeklyMatch[1] : '120';
      const twiceWeekly = twiceWeeklyMatch ? twiceWeeklyMatch[1] : '220';
      
      return (
        <div className="space-y-1">
          <div className="text-oldham-gold font-semibold">
            ${onceWeekly}/month
          </div>
          <div className="text-xs text-gray-400">
            1 session/week
          </div>
          <div className="text-oldham-gold font-semibold">
            ${twiceWeekly}/month
          </div>
          <div className="text-xs text-gray-400">
            2 sessions/week
          </div>
        </div>
      );
    }
    
    // Handle Junior Programme fees (once_weekly + twice_weekly + term)
    if (fees.once_weekly && fees.twice_weekly && fees.term) {
      // Extract just the main price number from the fee strings
      const onceWeeklyMatch = String(fees.once_weekly).match(/\$?(\d+)/);
      const twiceWeeklyMatch = String(fees.twice_weekly).match(/\$?(\d+)/);
      const termMatch = String(fees.term).match(/\$?(\d+)/);
      
      const onceWeekly = onceWeeklyMatch ? onceWeeklyMatch[1] : '160';
      const twiceWeekly = twiceWeeklyMatch ? twiceWeeklyMatch[1] : '240';
      const term = termMatch ? termMatch[1] : '660';
      
      return (
        <div className="space-y-1">
          <div className="text-oldham-gold font-semibold">
            ${onceWeekly}/month
          </div>
          <div className="text-xs text-gray-400">
            1 session/week
          </div>
          <div className="text-oldham-gold font-semibold">
            ${twiceWeekly}/month
          </div>
          <div className="text-xs text-gray-400">
            2 sessions/week
          </div>
          <div className="text-oldham-gold font-semibold">
            ${term}/term
          </div>
          <div className="text-xs text-gray-400">
            3 months, unlimited
          </div>
        </div>
      );
    }
    
    // Handle other fee structures
    if (fees.standard) {
      return <span className="text-oldham-gold font-semibold">${fees.standard}</span>;
    }
    
    return <span className="text-gray-400">TBA</span>;
  };
  
  // State for programs
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showProgramDetails, setShowProgramDetails] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      
      if (!apiUrl) {
        console.error('REACT_APP_API_URL is not configured');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrl.replace(/\/$/, '')}/api/programs`);
      const data = await response.json();
      
      if (data.success) {
        const sortedPrograms = data.programs.sort((a, b) => {
          const getMinAge = (ageRange) => {
            const match = ageRange.match(/(\d+)/);
            return match ? parseInt(match[1]) : 0;
          };
          return getMinAge(a.age_range) - getMinAge(b.age_range);
        });
        setPrograms(sortedPrograms);
      } else {
        console.error('Error fetching programs:', data.error);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProgramSignup = (program) => {
    // Check if user is logged in
    if (!currentUser) {
      alert('Please sign up for an account to register for programs.');
      window.location.href = '/register';
      return;
    }
    
    // Show signup modal with payment flow
    setSelectedProgram(program);
    setShowSignupModal(true);
  };


  // Membership benefits data
  const membershipBenefits = [
    {
      icon: <CheckIcon className="w-6 h-6 text-oldham-gold" />,
      title: "Competition Access",
      description: "Participate in club competitions and events"
    },
    {
      icon: <UserGroupIcon className="w-6 h-6 text-oldham-blueLight" />,
      title: "Training Programs",
      description: "Access to all youth and adult training programs"
    },
    {
      icon: <StarIcon className="w-6 h-6 text-oldham-red" />,
      title: "Data Analysis",
      description: "Performance tracking and improvement insights"
    },
    {
      icon: <HeartIcon className="w-6 h-6 text-oldham-gold" />,
      title: "Community Support",
      description: "Join our supportive athletic community"
    }
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-oldham-blue/20 to-oldham-red/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Programmes & <span className="text-oldham-gold">Membership</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Join our comprehensive training programs and become part of Singapore's premier athletics community.
            </p>
          </div>
        </div>
      </div>

        {/* Membership Benefits */}
        <section className="py-20 bg-gray-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-6">
                Membership <span className="text-oldham-gold">Benefits</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Unlock exclusive access to training programs, competitions, and our supportive community.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {membershipBenefits.map((benefit, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-oldham-gold/50 transition-all duration-300 text-center">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Training Programs */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-6">
                Training <span className="text-oldham-blueLight">Programs</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Structured programs designed for athletes of all ages and skill levels.
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-oldham-gold"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {programs.map((program) => (
                  <div key={program.id} className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 hover:border-oldham-gold/50 transition-all duration-300 cursor-pointer h-72 flex flex-col relative" onClick={() => {setSelectedProgram(program); setShowProgramDetails(true);}}>
                    {/* Details Button - Top Right */}
                    <button
                      className="absolute top-4 right-4 bg-oldham-gold/20 hover:bg-oldham-gold/30 text-oldham-gold rounded-full p-2 transition-all duration-300 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProgram(program);
                        setShowProgramDetails(true);
                      }}
                    >
                      <span className="text-sm font-semibold">i</span>
                    </button>
                    
                    {/* Simple Card Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="text-xl font-bold text-white mb-3 pr-12">{program.name}</h3>
                      <p className="text-gray-300 text-sm mb-4">{typeof program.age_range === 'string' ? program.age_range : 'Contact us for more information'}</p>
                      <div className="mb-4">
                        {renderFees(program.fees || program.fee)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Program Details Modal */}
        {showProgramDetails && selectedProgram && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl p-8 max-w-2xl w-full border border-gray-700/50 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">{selectedProgram.name}</h3>
                <button
                  onClick={() => setShowProgramDetails(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Pricing Section */}
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Pricing</h4>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    {renderFees(selectedProgram.fees || selectedProgram.fee)}
                  </div>
                </div>

                {/* Details Section */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Program Details</h4>
                    <div className="space-y-3">
                      <div className="flex items-start text-gray-300">
                        <UserGroupIcon className="w-5 h-5 mr-3 text-oldham-blueLight flex-shrink-0 mt-0.5" />
                        <span className="text-sm leading-relaxed">{typeof selectedProgram.age_range === 'string' ? selectedProgram.age_range : 'Contact us for more information'}</span>
                      </div>
                      <div className="flex items-start text-gray-300">
                        <ClockIcon className="w-5 h-5 mr-3 text-oldham-gold flex-shrink-0 mt-0.5" />
                        <span className="text-sm leading-relaxed">{formatSchedule(selectedProgram.schedule)}</span>
                      </div>
                      <div className="flex items-start text-gray-300">
                        <MapPinIcon className="w-5 h-5 mr-3 text-oldham-red flex-shrink-0 mt-0.5" />
                        <span className="text-sm leading-relaxed">{formatVenue(selectedProgram.venue)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Description</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {selectedProgram.description}
                    </p>
                  </div>
                </div>

                {/* Benefits Section */}
                {selectedProgram.benefits && selectedProgram.benefits.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Benefits</h4>
                    <ul className="space-y-2">
                      {selectedProgram.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start text-gray-300">
                          <CheckIcon className="w-4 h-4 mr-2 text-oldham-gold flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Features Section */}
                {selectedProgram.features && selectedProgram.features.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Features</h4>
                    <ul className="space-y-2">
                      {selectedProgram.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-gray-300">
                          <StarIcon className="w-4 h-4 mr-2 text-oldham-gold flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Coaches Section */}
                {selectedProgram.coaches && (
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Coaches</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {selectedProgram.coaches}
                    </p>
                  </div>
                )}

                {/* Equipment Section */}
                {selectedProgram.equipment_needed && (
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Equipment Needed</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {selectedProgram.equipment_needed}
                    </p>
                  </div>
                )}
              </div>

              {/* WhatsApp Info */}
              <div className="bg-oldham-gold/10 border border-oldham-gold/30 rounded-lg p-4 mt-6">
                <div className="flex items-start">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-oldham-gold mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-oldham-gold font-semibold mb-1">Registration via WhatsApp</h4>
                    <p className="text-gray-300 text-sm">
                      Click the button below to contact us directly on WhatsApp for program registration. 
                      We'll guide you through the signup process, verify your membership status, and answer any questions you may have.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-8 pt-6 border-t border-gray-700/50">
                <button
                  onClick={() => setShowProgramDetails(false)}
                  className="flex-1 bg-gray-600 text-white hover:bg-gray-500 font-semibold py-3 px-6 rounded-lg transition-all duration-300"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowProgramDetails(false);
                    handleProgramSignup(selectedProgram);
                  }}
                  className="flex-1 bg-oldham-gold text-gray-900 hover:bg-oldham-gold/90 font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                  {currentUser ? 'Register Now' : 'Register (Login Required)'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ProgramSignupModal
          program={selectedProgram}
          isOpen={showSignupModal}
          onClose={() => setShowSignupModal(false)}
          onSuccess={() => {
            setShowSignupModal(false);
            setSelectedProgram(null);
          }}
        />
      </div>
  );
};

export default Programmes;